import { msg } from "@omegagrid/localize";
import rp, { SqlCompletionOptions, SqlDatabaseRole, SqlEditorAdapter, SqlEditorOptions, SqlExecOptions, SqlExecCallbackArgs, SqlParseResult, SqlSchemaCatalog, SQL_DEPLOYMENT_PSEUDO_VARIABLES, SQL_SCHEMA_CATALOG } from "@reporting/rp-app-host";
import { GlobalRights, HistoryItem } from '@reporting/rp-types/core';
import { DeploymentThreadTypes } from "@reporting/rp-types/deployment";
import { Thread } from "@reporting/rp-types/etl";

export class ThreadSqlEditorAdapter extends SqlEditorAdapter {

	/** deployment threads know only their placeholders: no engine @variables and no {{macros}} */
	getCompletionOptions(): SqlCompletionOptions {
		return { internalVariables: false, macros: false, pseudoVariables: SQL_DEPLOYMENT_PSEUDO_VARIABLES };
	}

	/** a deployment thread targets the databases of the platform (of any customer), so the objects stay the static catalogue of the repository */
	getSchemaCatalog(): SqlSchemaCatalog {
		return SQL_SCHEMA_CATALOG;
	}

	/** the names without a prefix resolve in the database the thread type is deployed to (see getOptions()) */
	getSchemaDatabase(): SqlDatabaseRole | null {
		switch (this.thread.type) {
			case DeploymentThreadTypes.Custom:
			case DeploymentThreadTypes.DbData:
				return 'data';
			case DeploymentThreadTypes.DbMaster:
				return 'master';
			case DeploymentThreadTypes.DbSuper:
			case DeploymentThreadTypes.Scripts:
				return 'super';
			default:
				return null;
		}
	}

	constructor(
		protected thread: Thread,
	) { super() }

	async getContent() : Promise<string> {
		if (this.thread.id_thread == 0) return null;
		return rp.ms('deployment').get<string>(`threads/${this.thread.id_thread}/content`);
	}

	async getHistoryContent(idz: number): Promise<string> {
		return rp.ms('deployment').get<string>(`threads/${this.thread.id_thread}/history/${idz}`);
	}

	async parse(sql: string) : Promise<SqlParseResult> {
		return rp.ms('server').post<SqlParseResult>('parse', sql);
	}

	async exec(sql: string, options?: SqlExecOptions, cb?: (args: SqlExecCallbackArgs) => void) : Promise<void> {
		await this.execStream(rp.ms('deployment'), `exec/${this.thread.id_thread}`, sql, options, cb, {
			database: options?.database
		});
	}

	async save(sql: string) {
		await rp.ms('deployment').patch(`threads/${this.thread.id_thread}/content`, sql, { accounting: this.thread.accounting });
	}

	getHistoryItems(): Promise<HistoryItem[]> {
		return rp.ms('deployment').get<HistoryItem[]>(`threads/${this.thread.id_thread}/history`);
	}

	getCurrentItem(): HistoryItem {
		return this.thread;
	}

	getOptions(): SqlEditorOptions {
		const isReadonly = rp.userProvider.hasRight(GlobalRights.DeploymentRead) && !rp.userProvider.hasRight(GlobalRights.Deployment);
		let currentDbName = '';
		switch (this.thread.type) {
			case DeploymentThreadTypes.Custom:
			case DeploymentThreadTypes.DbData:
				currentDbName = rp.accountingProvider.accounting.data_db_name;
				break;
			case DeploymentThreadTypes.DbSuper:
			case DeploymentThreadTypes.Scripts:
				currentDbName = rp.accountingProvider.accounting.super_db_name;
				break;
			case DeploymentThreadTypes.DbMaster:
				currentDbName = rp.accountingProvider.accounting.master_db_name;
				break;
		}
		return this.createOptions({
			history: true,
			readOnly: isReadonly,
			toolbar: isReadonly ? {
				exec: { enabled: false },
				top: { enabled: false },
				save: { enabled: false },
				discard: { enabled: false },
			} : {
				exec: { items: null	},
				stop: { disabled: true },
				save: { text: msg('Save') },
				discard: { text: msg('Discard') },
				database: {
					enabled: true,
					value: 'current',
					items: [
						{k: 'current', v: `${msg('Current database')} (${currentDbName})`, s: true},
						{k: 'all', v: msg('All databases')},
					],
				},
			}
		});
	}

}
